<script>
import UpdateIcon from './UpdateIcon.vue'
import { unseenState, loadUnseen, markSeen } from './unseenStore'

const DEFAULT_COVER = require('@/assets/img/system-updates/popup-cover.png')

// Окно «Новое обновление» при входе в кабинет: одно самое свежее непросмотренное
// обновление. Любое закрытие отмечает его просмотренным, повторно оно не покажется.
export default {
	name: 'SystemUpdatePopup',
	components: { UpdateIcon },
	data() {
		return {
			state: unseenState,
			// Обложку грузим отдельно: битая ссылка из админки не должна оставить пустую шапку
			coverFailed: false,
			lastFocused: null,
		}
	},
	computed: {
		update() {
			return this.state.popup
		},
		coverUrl() {
			return this.update && this.update.cover_url && !this.coverFailed
				? this.update.cover_url
				: DEFAULT_COVER
		},
		highlights() {
			return (
				(this.update && Array.isArray(this.update.highlights) ? this.update.highlights : [])
					.filter((item) => item && item.title)
					// В окне — только первые 4 пункта, остальные видны на странице обновлений
					.slice(0, 4)
			)
		},
	},
	watch: {
		update(value, old) {
			if (value && (!old || old.id !== value.id)) this.coverFailed = false
			this.toggleOpen(Boolean(value))
		},
		// Тексты окна и колокольчика приходят на языке запроса — после смены языка перезапрашиваем
		'$i18n.locale'() {
			if (this.update || this.state.items.length) loadUnseen({ force: true })
		},
	},
	mounted() {
		loadUnseen()
		if (this.update) this.toggleOpen(true)
	},
	beforeDestroy() {
		this.toggleOpen(false)
	},
	methods: {
		toggleOpen(open) {
			if (open) {
				this.lastFocused = document.activeElement
				document.body.classList.add('su-popup-open')
				document.addEventListener('keydown', this.onKeydown)
				this.$nextTick(() => {
					if (this.$refs.primary) this.$refs.primary.focus({ preventScroll: true })
				})
			} else {
				document.body.classList.remove('su-popup-open')
				document.removeEventListener('keydown', this.onKeydown)
				if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
					this.lastFocused.focus({ preventScroll: true })
				}
				this.lastFocused = null
			}
		},
		onKeydown(event) {
			if (event.key === 'Escape') {
				this.close()
				return
			}
			// Фокус не уходит из окна под затемнение
			if (event.key === 'Tab' && this.$refs.dialog) {
				const focusable = this.$refs.dialog.querySelectorAll('button, [href]')
				if (!focusable.length) return
				const first = focusable[0]
				const last = focusable[focusable.length - 1]
				if (event.shiftKey && document.activeElement === first) {
					event.preventDefault()
					last.focus()
				} else if (!event.shiftKey && document.activeElement === last) {
					event.preventDefault()
					first.focus()
				}
			}
		},
		close() {
			if (this.update) markSeen(this.update.id)
		},
		openDetails() {
			if (!this.update) return
			const id = this.update.id
			markSeen(id)
			this.$router.push('/' + this.$i18n.locale + '/updates/' + id)
		},
	},
}
</script>

<template>
	<transition name="su-popup">
		<div v-if="update" class="su-popup" @click.self="close">
			<div
				ref="dialog"
				class="su-popup__dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="su-popup-title"
			>
				<div class="su-popup__scroll">
					<div class="su-popup__head">
						<!-- Картинка уходит под список и растворяется в белом фоне окна -->
						<div class="su-popup__art" aria-hidden="true">
							<img
								:key="coverUrl"
								class="su-popup__cover"
								:src="coverUrl"
								alt=""
								@error="coverFailed = true"
							/>
							<div class="su-popup__shade"></div>
						</div>
						<button
							type="button"
							class="su-popup__close"
							:aria-label="$t('system_updates.close')"
							@click="close"
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2.2"
								stroke-linecap="round"
							>
								<path d="M6 6l12 12M18 6 6 18" />
							</svg>
						</button>
						<div class="su-popup__head-content">
							<span class="su-popup__badge">{{ $t('system_updates.popup_badge') }}</span>
							<h2 id="su-popup-title" class="su-popup__title">{{ update.title }}</h2>
							<p v-if="update.summary" class="su-popup__summary">{{ update.summary }}</p>
						</div>
					</div>

					<div class="su-popup__body">
						<ul v-if="highlights.length" class="su-popup__list">
							<li v-for="(item, index) in highlights" :key="index" class="su-popup__item">
								<span class="su-popup__icon">
									<UpdateIcon :name="item.icon" :size="22" />
								</span>
								<span class="su-popup__item-text">
									<span class="su-popup__item-title">{{ item.title }}</span>
									<span v-if="item.subtitle" class="su-popup__item-subtitle">{{
										item.subtitle
									}}</span>
								</span>
							</li>
						</ul>

						<div class="su-popup__actions">
							<button
								ref="primary"
								type="button"
								class="su-popup__btn su-popup__btn--primary"
								@click="openDetails"
							>
								{{ $t('system_updates.popup_more') }}
								<svg
									width="18"
									height="18"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<path d="M5 12h14M13 6l6 6-6 6" />
								</svg>
							</button>
							<button type="button" class="su-popup__btn su-popup__btn--ghost" @click="close">
								{{ $t('system_updates.popup_ok') }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<style>
body.su-popup-open {
	overflow: hidden;
}

.su-popup {
	position: fixed;
	inset: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
	background: rgba(21, 29, 72, 0.45);
	backdrop-filter: blur(2px);
}

.su-popup__dialog {
	position: relative;
	width: 100%;
	max-width: 620px;
	/* не липнет к краям экрана: по 40px сверху и снизу */
	max-height: calc(100vh - 80px);
	max-height: calc(100dvh - 80px);
	display: flex;
	border-radius: 20px;
	background: #ffffff;
	box-shadow: 0 24px 80px rgba(21, 29, 72, 0.35);
	overflow: hidden;
}

.su-popup__scroll {
	position: relative;
	width: 100%;
	overflow-y: auto;
	overscroll-behavior: contain;
	/* прокрутка есть, полосы нет */
	scrollbar-width: none;
	-ms-overflow-style: none;
}

.su-popup__scroll::-webkit-scrollbar {
	display: none;
}

.su-popup__head {
	position: relative;
	padding: 98px 28px 72px;
	color: #ffffff;
	display: flex;
	align-items: flex-end;
}

/* Слой картинки выше шапки: заходит под список и к низу плавно уходит в белый */
.su-popup__art {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	/* вся шапка + хвост под список; растворяется только хвост */
	bottom: -200px;
	overflow: hidden;
	pointer-events: none;
	-webkit-mask-image: linear-gradient(180deg, #000 0%, #000 calc(100% - 230px), transparent 100%);
	mask-image: linear-gradient(180deg, #000 0%, #000 calc(100% - 230px), transparent 100%);
}

.su-popup__cover {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	/* в шапке видна подводная часть с рыбой, а не небо */
	object-position: 62% 50%;
}

.su-popup__shade {
	position: absolute;
	inset: 0;
	background: linear-gradient(
			90deg,
			rgba(15, 79, 173, 0.78) 0%,
			rgba(21, 127, 238, 0.45) 55%,
			rgba(21, 127, 238, 0.08) 100%
		),
		linear-gradient(
			180deg,
			rgba(15, 79, 173, 0.25) 0%,
			rgba(15, 79, 173, 0.35) 45%,
			rgba(255, 255, 255, 0) 75%
		);
}

.su-popup__head-content {
	position: relative;
	min-width: 0;
}

.su-popup__close {
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 2;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	border: none;
	color: #ffffff;
	background: rgba(255, 255, 255, 0.18);
	cursor: pointer;
	transition: background 0.2s ease;
}

.su-popup__close:hover,
.su-popup__close:focus-visible {
	background: rgba(255, 255, 255, 0.32);
	outline: none;
}

.su-popup__badge {
	display: inline-block;
	margin-bottom: 14px;
	padding: 7px 14px;
	border-radius: 999px;
	background: #157fee;
	box-shadow: 0 4px 14px rgba(15, 79, 173, 0.35);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	line-height: 1;
}

.su-popup__title {
	margin: 0 0 10px;
	font-size: 26px;
	font-weight: 700;
	line-height: 1.2;
	color: #ffffff;
	overflow-wrap: anywhere;
}

.su-popup__summary {
	margin: 0;
	font-size: 15px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.92);
	overflow-wrap: anywhere;
	white-space: pre-line;
}

.su-popup__body {
	position: relative;
	margin-top: -48px;
	padding: 0 24px 24px;
}

.su-popup__list {
	list-style: none;
	margin: 0 0 20px;
	padding: 8px 20px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.86);
	-webkit-backdrop-filter: blur(10px);
	backdrop-filter: blur(10px);
	box-shadow: 0 8px 30px rgba(21, 127, 238, 0.14);
}

.su-popup__item {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 12px 0;
}

.su-popup__item + .su-popup__item {
	border-top: 1px solid #eef3fa;
}

.su-popup__icon {
	flex: 0 0 44px;
	width: 44px;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 12px;
	color: #157fee;
	background: #eaf4ff;
}

.su-popup__item-text {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.su-popup__item-title {
	font-size: 15px;
	font-weight: 600;
	line-height: 1.35;
	color: #151d48;
	overflow-wrap: anywhere;
}

.su-popup__item-subtitle {
	font-size: 13px;
	line-height: 1.4;
	color: #6b7a99;
	overflow-wrap: anywhere;
}

.su-popup__actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.su-popup__btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 48px;
	padding: 12px 20px;
	border-radius: 12px;
	font-size: 15px;
	font-weight: 600;
	line-height: 1.2;
	text-align: center;
	cursor: pointer;
	transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.su-popup__btn--primary {
	border: 1px solid #157fee;
	color: #ffffff;
	background: #157fee;
}

.su-popup__btn--primary:hover,
.su-popup__btn--primary:focus-visible {
	background: #0f6bd0;
	border-color: #0f6bd0;
	outline: none;
}

.su-popup__btn--ghost {
	border: 1px solid #d6e4f5;
	color: #157fee;
	background: #ffffff;
}

.su-popup__btn--ghost:hover,
.su-popup__btn--ghost:focus-visible {
	border-color: #157fee;
	background: #f4f9ff;
	outline: none;
}

.su-popup-enter-active,
.su-popup-leave-active {
	transition: opacity 0.25s ease;
}

.su-popup-enter-active .su-popup__dialog,
.su-popup-leave-active .su-popup__dialog {
	transition: transform 0.25s ease;
}

.su-popup-enter,
.su-popup-leave-to {
	opacity: 0;
}

.su-popup-enter .su-popup__dialog,
.su-popup-leave-to .su-popup__dialog {
	transform: translateY(16px) scale(0.98);
}

@media (max-width: 575px) {
	.su-popup {
		padding: 12px;
	}

	.su-popup__dialog {
		max-height: calc(100vh - 24px);
		max-height: calc(100dvh - 24px);
		border-radius: 16px;
	}

	.su-popup__head {
		padding: 56px 20px 64px;
	}

	.su-popup__title {
		font-size: 21px;
	}

	.su-popup__summary {
		font-size: 14px;
	}

	.su-popup__body {
		padding: 0 14px 16px;
	}

	.su-popup__list {
		padding: 4px 14px;
	}

	.su-popup__item {
		gap: 12px;
	}

	.su-popup__icon {
		flex-basis: 38px;
		width: 38px;
		height: 38px;
		border-radius: 10px;
	}
}

@media (prefers-reduced-motion: reduce) {
	.su-popup-enter-active,
	.su-popup-leave-active,
	.su-popup-enter-active .su-popup__dialog,
	.su-popup-leave-active .su-popup__dialog {
		transition: none;
	}
}

/* Страховка от слишком длинных текстов: окно не должно разрастаться */
.su-popup__title {
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.su-popup__summary {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.su-popup__item-title,
.su-popup__item-subtitle {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.su-popup__btn:focus {
	outline: none;
}

.su-popup__btn:focus-visible {
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.3);
}
</style>
